import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import apiClient from "../../axiosInstance";
import showAlert from "../../components/Common/ShowAlert";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const RolePermissions = () => {
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Role Management & Permission", link: "#" },
  ]);

  const [roles, setRoles] = useState([]);
  const [summary, setSummary] = useState({});
  const [loadingRoles, setLoadingRoles] = useState(false);

  const [selectedRole, setSelectedRole] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  const [createModal, setCreateModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");

  const fetchRoles = async () => {
    setLoadingRoles(true);
    try {
      const result = await apiClient.get("/roles/getAllRoles");
      const roleRows = Array.isArray(result.data.response)
        ? result.data.response
        : [];
      setRoles(roleRows);

      const summaryMap = {};
      await Promise.all(
        roleRows.map(async (role) => {
          try {
            const rolePermissionsResponse = await apiClient.get(
              `/permissions/getRolePermissions/${role.id}`,
            );
            const rows = Array.isArray(rolePermissionsResponse.data.response)
              ? rolePermissionsResponse.data.response
              : [];
            const assigned = rows.filter(
              (item) => Number(item.assigned) === 1,
            ).length;
            summaryMap[role.id] = {
              total: rows.length,
              assigned,
            };
          } catch (_err) {
            summaryMap[role.id] = {
              total: 0,
              assigned: 0,
            };
          }
        }),
      );
      setSummary(summaryMap);
    } catch (err) {
      console.error("Error fetching roles:", err);
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchPermissionsForRole = async (roleId) => {
    if (!roleId) {
      setPermissions([]);
      setSelectedPermissionIds([]);
      return;
    }

    setLoadingPermissions(true);
    try {
      const result = await apiClient.get(
        `/permissions/getRolePermissions/${roleId}`,
      );
      const rows = Array.isArray(result.data.response) ? result.data.response : [];
      setPermissions(rows);
      setSelectedPermissionIds(
        rows.filter((item) => Number(item.assigned) === 1).map((item) => item.id),
      );
    } catch (err) {
      console.error("Error fetching role permissions:", err);
      setPermissions([]);
      setSelectedPermissionIds([]);
    } finally {
      setLoadingPermissions(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const groupedPermissions = useMemo(
    () =>
      permissions.reduce((acc, item) => {
        if (!acc[item.feature]) {
          acc[item.feature] = [];
        }
        acc[item.feature].push(item);
        return acc;
      }, {}),
    [permissions],
  );

  const roleAccessRows = useMemo(() => {
    return Object.entries(groupedPermissions).map(([feature, items]) => {
      const readPermission = items.find((item) => item.action === "read") || null;
      const createPermission =
        items.find((item) => item.action === "create") || null;
      const updatePermission =
        items.find((item) => item.action === "update") || null;
      const deletePermission =
        items.find((item) => item.action === "delete") || null;

      return {
        feature,
        createPermission,
        readPermission,
        updatePermission,
        deletePermission,
      };
    });
  }, [groupedPermissions]);

  const togglePermission = (permissionId, checked) => {
    if (!permissionId) {
      return;
    }

    setSelectedPermissionIds((prev) => {
      if (checked) {
        if (prev.includes(permissionId)) {
          return prev;
        }
        return [...prev, permissionId];
      }
      return prev.filter((id) => id !== permissionId);
    });
  };

  const openEditModal = async (role) => {
    setSelectedRole(role);
    setEditModal(true);
    await fetchPermissionsForRole(role.id);
  };

  const handleSavePermissions = async () => {
    if (!selectedRole?.id) {
      return;
    }

    try {
      const response = await apiClient.post(
        `/permissions/assignRolePermissions/${selectedRole.id}`,
        {
          permission_ids: selectedPermissionIds,
        },
      );

      await showAlert({
        title: "Role Permissions",
        text: response.data.message,
        icon: response.data.status,
      });

      if (response.data.status === "success") {
        setEditModal(false);
        fetchRoles();
      }
    } catch (err) {
      console.error("Error assigning permissions:", err);
      await showAlert({
        title: "Role Permissions",
        text: err?.response?.data?.message || "Failed to assign permissions",
        icon: "error",
      });
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) {
      await showAlert({
        title: "Role",
        text: "Role name is required",
        icon: "error",
      });
      return;
    }

    try {
      const response = await apiClient.post("/roles/createRole", {
        role_name: newRoleName,
      });

      await showAlert({
        title: "Role",
        text: response.data.message,
        icon: response.data.status,
      });

      if (response.data.status === "success") {
        setCreateModal(false);
        setNewRoleName("");
        fetchRoles();
      }
    } catch (err) {
      console.error("Error creating role:", err);
      await showAlert({
        title: "Role",
        text: err?.response?.data?.message || "Failed to create role",
        icon: "error",
      });
    }
  };

  const initials = ["A", "M", "S", "D"];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Roles and Permissions"
          breadcrumbItems={breadcrumbItems}
        />

        <Card>
          <CardBody>
            <div className="d-flex justify-content-end mb-3">
              <button
                className="btn btn-primary"
                onClick={() => setCreateModal(true)}
              >
                <i className="ri-add-line me-1"></i>
                Add New Role
              </button>
            </div>

            {loadingRoles ? (
              <div className="text-muted text-center py-5">Loading roles...</div>
            ) : roles.length === 0 ? (
              <div className="text-muted text-center py-5">No roles found.</div>
            ) : (
              <div className="row g-3">
                {roles.map((role) => {
                  const roleSummary = summary[role.id] || { total: 0, assigned: 0 };
                  return (
                    <div className="col-12 col-sm-6 col-lg-4 col-xl-3" key={role.id}>
                      <Card className="h-100 border">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className="d-inline-flex align-items-center justify-content-center rounded-3 bg-primary text-white"
                                style={{ width: "40px", height: "40px" }}
                              >
                                <i className="ri-shield-user-line fs-5"></i>
                              </span>
                              <h5 className="mb-0">{role.name}</h5>
                            </div>
                            <i className="ri-more-2-fill text-muted"></i>
                          </div>

                          <div className="d-flex align-items-center gap-2 mb-3">
                            <div className="d-flex">
                              {initials.map((initial, avatarIndex) => (
                                <span
                                  key={`${role.id}-${avatarIndex}`}
                                  className="rounded-circle bg-light border d-inline-flex align-items-center justify-content-center"
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    marginLeft: avatarIndex === 0 ? 0 : "-8px",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {initial}
                                </span>
                              ))}
                            </div>
                            <span className="text-muted">
                              Total {roleSummary.assigned} permissions
                            </span>
                          </div>

                          <button
                            className="btn btn-outline-secondary w-100"
                            onClick={() => openEditModal(role)}
                          >
                            <i className="ri-settings-3-line me-1"></i>
                            Edit Role
                          </button>
                        </CardBody>
                      </Card>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>

        <Modal
          isOpen={editModal}
          toggle={() => setEditModal(false)}
          centered
          size="xl"
          backdrop="static"
        >
          <ModalHeader toggle={() => setEditModal(false)}>Edit Role</ModalHeader>
          <ModalBody>
            <h5 className="mb-3">Role Access</h5>

            {loadingPermissions ? (
              <div className="text-muted py-4">Loading permissions...</div>
            ) : roleAccessRows.length === 0 ? (
              <div className="text-muted py-4">No permissions available.</div>
            ) : (
              <div className="row g-3">
                {roleAccessRows.map((row) => {
                  const createChecked = row.createPermission
                    ? selectedPermissionIds.includes(row.createPermission.id)
                    : false;
                  const readChecked = row.readPermission
                    ? selectedPermissionIds.includes(row.readPermission.id)
                    : false;

                  const updateChecked = row.updatePermission
                    ? selectedPermissionIds.includes(row.updatePermission.id)
                    : false;

                  const deleteChecked = row.deletePermission
                    ? selectedPermissionIds.includes(row.deletePermission.id)
                    : false;

                  return (
                    <div key={row.feature} className="col-12 col-lg-6">
                      <div className="border rounded p-3 h-100">
                        <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                          <h6 className="mb-0 text-capitalize">{row.feature}</h6>
                          <div className="d-flex gap-2 flex-wrap">
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                createChecked
                                  ? "btn-primary"
                                  : "btn-outline-secondary"
                              }`}
                              disabled={!row.createPermission}
                              title="Create"
                              onClick={() =>
                                togglePermission(
                                  row.createPermission?.id,
                                  !createChecked,
                                )
                              }
                            >
                              <i className="ri-add-line"></i>
                            </button>

                            <button
                              type="button"
                              className={`btn btn-sm ${
                                readChecked ? "btn-primary" : "btn-outline-secondary"
                              }`}
                              disabled={!row.readPermission}
                              title="Read"
                              onClick={() =>
                                togglePermission(
                                  row.readPermission?.id,
                                  !readChecked,
                                )
                              }
                            >
                              <i className="ri-eye-line"></i>
                            </button>

                            <button
                              type="button"
                              className={`btn btn-sm ${
                                updateChecked
                                  ? "btn-primary"
                                  : "btn-outline-secondary"
                              }`}
                              disabled={!row.updatePermission}
                              title="Update"
                              onClick={() =>
                                togglePermission(
                                  row.updatePermission?.id,
                                  !updateChecked,
                                )
                              }
                            >
                              <i className="ri-edit-line"></i>
                            </button>

                            <button
                              type="button"
                              className={`btn btn-sm ${
                                deleteChecked
                                  ? "btn-primary"
                                  : "btn-outline-secondary"
                              }`}
                              disabled={!row.deletePermission}
                              title="Delete"
                              onClick={() =>
                                togglePermission(
                                  row.deletePermission?.id,
                                  !deleteChecked,
                                )
                              }
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <button className="btn btn-light" onClick={() => setEditModal(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSavePermissions}>
              Save
            </button>
          </ModalFooter>
        </Modal>

        <Modal
          isOpen={createModal}
          toggle={() => setCreateModal(false)}
          centered
          backdrop="static"
        >
          <ModalHeader toggle={() => setCreateModal(false)}>
            Add New Role
          </ModalHeader>
          <ModalBody>
            <label className="form-label">Role Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter role name"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <button className="btn btn-light" onClick={() => setCreateModal(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleCreateRole}>
              Save
            </button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default RolePermissions;
